import { useCallback, useState } from "react";
import { useLookupValueUsage } from "@/pages/settings/hooks";
import { OptionItem } from "../utils/fieldFormUtils";

type UsageQuery = {
  route: "settings/industries/usage" | "settings/subIndustries/usage";
  pathParams: Record<string, string>;
} | null;

interface HidePrompt {
  item: OptionItem;
  resolve: (proceed: boolean) => void;
}

/** Hide gate: if records use the value, pause on a confirmation before hiding. buildUsageQuery returning null skips the check. */
export const useHideValueGate = (
  buildUsageQuery: (item: OptionItem) => UsageQuery
) => {
  const fetchUsage = useLookupValueUsage();
  const [hidePrompt, setHidePrompt] = useState<HidePrompt | null>(null);

  const onBeforeHide = useCallback(
    async (item: OptionItem): Promise<boolean> => {
      const query = buildUsageQuery(item);
      if (!query) return true;
      let count: number;
      try {
        count = await fetchUsage(query.route, query.pathParams);
      } catch {
        // Usage check is advisory (hide is staged, idempotent, reversible) —
        // don't block the user on a transient lookup failure.
        return true;
      }
      if (count === 0) return true;
      return new Promise<boolean>((resolve) =>
        setHidePrompt({ item, resolve })
      );
    },
    [fetchUsage, buildUsageQuery]
  );

  const cancelHide = useCallback(() => {
    setHidePrompt((prev) => {
      prev?.resolve(false);
      return null;
    });
  }, []);

  const confirmHide = useCallback(() => {
    setHidePrompt((prev) => {
      prev?.resolve(true);
      return null;
    });
  }, []);

  return { onBeforeHide, hidePrompt, cancelHide, confirmHide };
};
